/**
 * "Gordura · foto" — the photo body-fat series, as its OWN labelled series in
 * Measurements and on Progress (`D23`, and this plan's `D05`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ── IT IS A SECOND SERIES, NEVER A SECOND LINE ON THE FIRST ────────────────
 * The manual `body_fat` measurements and these estimates differ
 * systematically: one is a caliper or a scale, the other is the RFM equation
 * over two photographs. `D23` is explicit that the photo number "appears in
 * Measurements as its own labelled series, never mixed into the manually
 * entered one". So this card sits BESIDE `BodyFatCard` and reads a different
 * route; nothing here touches the manual series, and `body-assessment.series_name`
 * is the label that keeps the two tellable apart.
 *
 * ── TWO POINTS ARE JOINED ONLY IF BOTH REFUSALS PASS ───────────────────────
 * A trend's failure mode is not a crash. It is a perfectly rendered difference
 * between two numbers that says nothing about the body.
 *
 * **1. `comparable === true`, never `!== false`.** `comparable: false` means
 * the photo protocol failed — loose clothing, shoes, hair over the outline —
 * and the contract says the number "cannot be compared with the others … keep
 * it OFF every trend". `null` is "not judged", which is not "comparable": a
 * row that never settled must not quietly earn a place on the trend. So
 * {@link toPhotoSeries} plots `=== true` and puts everything else under
 * {@link PhotoSeries.excluded} — SHOWN, because it is still this person's own
 * measurement, and badged only where the judgement was actually made.
 *
 * **2. The `pipelineVersion` must match.** A new version changes the number
 * without the body changing (`bodyAssessment.pipelineVersion`, `D23`), so a
 * difference across a bump is a release note drawn as a change in someone's
 * body. Nothing on the wire flags that pair and both numbers render perfectly,
 * which is exactly why the rule has to be in the pure function and not in a
 * reader's memory. The difference comes back when `/v1/recompute` has replayed
 * the history, and `series_pipeline_note` says so.
 *
 * `compare.tsx` refuses a two-point delta for the same two reasons, in
 * `protocolBlocks` and `pipelineBlocks`. They are deliberately NOT imported
 * from there: that module is the comparison screen and pulls the history list,
 * the photo query and an alert dialog behind it, and this card is mounted by
 * the profile and by Progress, neither of which should carry the body
 * overlay's tree. What is shared instead is the thing that would actually
 * drift — the wording, through the message catalogue — and the rules are
 * pinned by `__tests__/photo-series.test.ts` on this side.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── A NUMBER NEVER APPEARS WITHOUT ITS RANGE ───────────────────────────────
 * The row is rendered from `presentBodyResult`, exactly as the history row is,
 * so the percentage, the low and the high stay the three parameters of ONE
 * message and no component here CAN print a bare percentage. `present.ts` is
 * pure and imports nothing heavier than the contract, so reusing it costs the
 * profile nothing.
 *
 * ── HIDDEN UNDER 15, WITH NOTHING TO EXPLAIN ───────────────────────────────
 * `bodycomp` refuses under {@link BODY_ASSESSMENT_MIN_AGE} and the overlay
 * carries the explanation (`eligibility.ts`, `D24`). A card offering a feature
 * that cannot be reached is worse than no card, so
 * {@link photoSeriesVisible} hides it — and it hides it while the profile is
 * unread too, because the two wrong guesses are not symmetric.
 */

import { bodyAssessmentsOptions } from "@repo/client/data/body";
import { profileOptions } from "@repo/client/data/profile";
import {
  type AppLocale,
  ageFrom,
  formatDay,
  parseCalendarDay,
} from "@repo/client/format";
import {
  BODY_ASSESSMENT_MIN_AGE,
  type BodyAssessment,
} from "@repo/contract/training";
import {
  "body-assessment.empty_body" as emptyBodyText,
  "body-assessment.not_comparable_label" as notComparableLabelText,
  "body-assessment.series_delta" as seriesDeltaText,
  "body-assessment.series_excluded_title" as seriesExcludedTitleText,
  "body-assessment.series_name" as seriesNameText,
  "body-assessment.series_open" as seriesOpenText,
  "body-assessment.series_pipeline_note" as seriesPipelineNoteText,
  "body-assessment.trend_enough" as trendEnoughText,
} from "@repo/i18n";
import { ChevronRight } from "lucide-react";
import { useCallback } from "react";
import { useOpenLayer } from "../../app/layers";
import { useApi } from "../../data/api";
import { useDataQuery } from "../../data/query-client";
import { cx } from "../../lib/cx";
import { appLocale } from "../../lib/locale";
import { Icon } from "../../ui/controls";
import { appI18n } from "../auth";
import { type BodyPresentOptions, presentBodyResult } from "./present";

/** One settled estimate with a body-fat block. `date` is `completedAt ?? createdAt`. */
export interface PhotoSeriesPoint {
  readonly assessment: BodyAssessment;
  /** Kept raw: `null` is "not judged", and it is not `false`. */
  readonly comparable: boolean | null;
  /** ISO. `completedAt ?? createdAt` — the contract's own shape (`T88`). */
  readonly date: string;
  readonly high: number;
  readonly low: number;
  readonly percent: number;
  readonly pipelineVersion: string | null;
}

export interface PhotoSeries {
  /** Refused by the trend, newest first. Shown, and badged where judged. */
  readonly excluded: readonly PhotoSeriesPoint[];
  /**
   * `true` when at least one adjacent pair of {@link points} spans a
   * `pipelineVersion` change, so the card can say why a difference is missing.
   */
  readonly pipelineBreak: boolean;
  /** The trend: `comparable === true` only, OLDEST first. */
  readonly points: readonly PhotoSeriesPoint[];
}

/** A row of the card: a point, and the difference from the point before it. */
export interface PhotoSeriesRow {
  /**
   * `newer − older` in percentage points, or `null` — there is no point
   * before, or joining the two would draw a release note.
   */
  readonly delta: number | null;
  readonly point: PhotoSeriesPoint;
  /**
   * The day the difference is measured FROM — the older point's, never this
   * row's own. `series_delta` reads "{delta} pp since {date}", and passing the
   * row's own date made every row say it had changed since itself: a sentence
   * that renders perfectly, with the arithmetic correct, against a date that is
   * off by however far apart the two readings are. Found by a capture (T110),
   * not by a test — every unit test asserts the delta NUMBER, which was right.
   */
  readonly since: string | null;
}

function pointOf(assessment: BodyAssessment): PhotoSeriesPoint | null {
  const { bodyFat } = assessment;
  if (assessment.status !== "done" || bodyFat === null) {
    return null;
  }
  const date = assessment.completedAt ?? assessment.createdAt;
  if (!Number.isFinite(Date.parse(date))) {
    return null;
  }
  return {
    assessment,
    comparable: assessment.comparable,
    date,
    high: bodyFat.high,
    low: bodyFat.low,
    percent: bodyFat.percent,
    pipelineVersion: assessment.pipelineVersion,
  };
}

/** `true` when these two points may be joined at all (`D23`, rule 2). */
function joins(older: PhotoSeriesPoint, newer: PhotoSeriesPoint): boolean {
  return (
    older.pipelineVersion !== null &&
    newer.pipelineVersion !== null &&
    older.pipelineVersion === newer.pipelineVersion
  );
}

/**
 * The series, out of whatever `GET /me/body-assessments` answered.
 *
 * The list arrives newest first and is re-sorted by {@link PhotoSeriesPoint.date}
 * rather than trusted, because that date is `completedAt ?? createdAt` and the
 * server orders by `createdAt` alone — a row recomputed out of order would
 * otherwise put a difference the wrong way round with nothing to notice it.
 */
export function toPhotoSeries(
  assessments: readonly BodyAssessment[]
): PhotoSeries {
  const all: PhotoSeriesPoint[] = [];
  for (const assessment of assessments) {
    const point = pointOf(assessment);
    if (point !== null) {
      all.push(point);
    }
  }
  all.sort((a, b) => Date.parse(a.date) - Date.parse(b.date));
  const points = all.filter((p) => p.comparable === true);
  const excluded = all.filter((p) => p.comparable !== true).reverse();
  let pipelineBreak = false;
  for (let i = 1; i < points.length; i += 1) {
    const older = points[i - 1];
    const newer = points[i];
    if (older && newer && !joins(older, newer)) {
      pipelineBreak = true;
    }
  }
  return { excluded, pipelineBreak, points };
}

/** The card's rows: newest first, each carrying its difference or `null`. */
export function photoSeriesRows(
  points: readonly PhotoSeriesPoint[]
): readonly PhotoSeriesRow[] {
  const rows: PhotoSeriesRow[] = [];
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const point = points[i];
    const older = points[i - 1];
    if (!point) {
      continue;
    }
    const joined = older !== undefined && joins(older, point);
    rows.push({
      delta: joined && older ? point.percent - older.percent : null,
      point,
      since: joined && older ? older.date : null,
    });
  }
  return rows;
}

/**
 * The date the profile's completeness reads: the newest settled estimate that
 * produced a body-fat number, as `completedAt ?? createdAt` (`D05`).
 *
 * ⚠️ **`comparable === false` counts here, and that is deliberate.** It is
 * kept off the TREND above, because it cannot be compared with the others —
 * but it is still a body-fat number this person has, and asking them for a
 * measurement on the strength of a comparability flag is exactly the nagging
 * `D05` exists to stop. The two rules are different questions about the same
 * row, and this is the one place they disagree on purpose.
 */
export function photoLatestAt(
  assessments: readonly BodyAssessment[]
): string | null {
  let best: string | null = null;
  let bestTime = Number.NEGATIVE_INFINITY;
  for (const assessment of assessments) {
    const point = pointOf(assessment);
    if (point === null) {
      continue;
    }
    const time = Date.parse(point.date);
    if (time > bestTime) {
      best = point.date;
      bestTime = time;
    }
  }
  return best;
}

/**
 * Whether the card may be drawn at all: the person is at least
 * {@link BODY_ASSESSMENT_MIN_AGE}.
 *
 * `null`, or a date that does not parse, is `false`. Waiting is the safe side:
 * showing the card and taking it away again reads as a bug, and showing it to
 * a fourteen-year-old offers a feature the service refuses with 422.
 */
export function photoSeriesVisible(
  dateOfBirth: string | null | undefined,
  now: Date
): boolean {
  if (!dateOfBirth) {
    return false;
  }
  const age = ageFrom(parseCalendarDay(dateOfBirth), now);
  return Number.isFinite(age) && age >= BODY_ASSESSMENT_MIN_AGE;
}

/**
 * `Intl.NumberFormat` is not free to build and the card formats one number per
 * row, so the shape is cached by locale.
 *
 * `signDisplay: "exceptZero"` for `compare.tsx`'s reason: a difference has to
 * carry its sign, and "+0,0" reads as a change too small to print. This is a
 * second copy of that file's `formatDelta` and not an import of it — see the
 * header. **Recommended follow-up:** a `formatDecimal(value, locale, …)` in
 * `@repo/client/format` retires both (T107's decision 8 asked for the same
 * thing).
 */
const deltaFormats = new Map<string, Intl.NumberFormat>();

function formatSeriesDelta(value: number, locale: AppLocale): string {
  let format = deltaFormats.get(locale);
  if (!format) {
    format = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
      signDisplay: "exceptZero",
    });
    deltaFormats.set(locale, format);
  }
  return format.format(value);
}

/** The badge a judged-incomparable row carries. Deliberately a copy of
 *  `history.tsx`'s `NotComparableBadge` markup and not an import of it: that
 *  module is the overlay's history list. The RULE lives in
 *  {@link toPhotoSeries}; this is five lines of styling. */
function NotComparable() {
  return (
    <span
      className="inline-flex h-6 shrink-0 items-center rounded-full bg-warning-surface px-2 font-medium text-warning text-xs"
      data-testid="series-not-comparable"
    >
      {notComparableLabelText()}
    </span>
  );
}

function SeriesRow(props: {
  readonly delta: number | null;
  readonly excluded?: boolean;
  readonly options: BodyPresentOptions;
  readonly point: PhotoSeriesPoint;
  /** The older point's day — see {@link PhotoSeriesRow.since}. */
  readonly since: string | null;
  readonly topBorder: boolean;
}) {
  const { options, point } = props;
  const view = presentBodyResult(point.assessment, options);
  const value =
    view.kind === "done" && view.done.bodyFat !== null
      ? options.translate(
          view.done.bodyFat.value.key,
          view.done.bodyFat.value.params
        )
      : null;
  const day = formatDay(new Date(point.date), options.locale);
  return (
    <li
      className={cx(
        "flex flex-col gap-1 px-4 py-3 lg:px-5",
        props.topBorder && "border-line border-t"
      )}
      data-testid={props.excluded ? "series-excluded-row" : "series-row"}
    >
      <span className="flex flex-wrap items-center gap-2">
        <span className="text-ink-3 text-sm">{day}</span>
        {point.comparable === false ? <NotComparable /> : null}
      </span>
      {value === null ? null : (
        <span className="text-base text-ink" data-testid="series-value">
          {value}
        </span>
      )}
      {props.delta === null || props.since === null ? null : (
        <span
          className="text-ink-2 text-sm tabular-nums"
          data-testid="series-delta"
        >
          {seriesDeltaText({
            date: formatDay(new Date(props.since), options.locale),
            delta: formatSeriesDelta(props.delta, options.locale),
          })}
        </span>
      )}
    </li>
  );
}

export interface PhotoSeriesCardProps {
  readonly assessments: readonly BodyAssessment[];
  readonly className?: string;
  readonly onOpen: () => void;
}

/**
 * The card itself, over assessments the caller already read. Pure of Query so
 * the stories and the tests can drive it; {@link PhotoSeriesSection} is the
 * wired version the screens mount.
 */
export function PhotoSeriesCard(props: PhotoSeriesCardProps) {
  const locale = appLocale();
  const translate = appI18n.useTranslate();
  const options: BodyPresentOptions = { locale, translate };
  const series = toPhotoSeries(props.assessments);
  const rows = photoSeriesRows(series.points);
  const empty = rows.length === 0 && series.excluded.length === 0;

  return (
    <section
      className={cx("a-card overflow-hidden", props.className)}
      data-testid="photo-series"
    >
      <div className="px-4 pt-4 pb-2 lg:px-5">
        <p className="font-semibold text-base">{seriesNameText()}</p>
      </div>
      {empty ? (
        <p className="px-4 pb-3 text-ink-2 text-sm leading-snug lg:px-5">
          {emptyBodyText()}
        </p>
      ) : (
        <>
          <ul>
            {rows.map((row, i) => (
              <SeriesRow
                delta={row.delta}
                key={row.point.assessment.id}
                options={options}
                point={row.point}
                since={row.since}
                topBorder={i > 0}
              />
            ))}
          </ul>
          {series.pipelineBreak ? (
            <p
              className="mx-4 mb-2 rounded-md bg-surface-2 px-3.5 py-2.5 text-ink-2 text-sm leading-snug lg:mx-5"
              data-testid="series-pipeline-note"
            >
              {seriesPipelineNoteText()}
            </p>
          ) : null}
          {series.excluded.length > 0 ? (
            <div data-testid="series-excluded">
              <p className="px-4 pt-3 pb-1 font-medium text-ink-3 text-sm lg:px-5">
                {seriesExcludedTitleText()}
              </p>
              <ul>
                {series.excluded.map((point, i) => (
                  <SeriesRow
                    delta={null}
                    excluded
                    key={point.assessment.id}
                    options={options}
                    point={point}
                    since={null}
                    topBorder={i > 0}
                  />
                ))}
              </ul>
            </div>
          ) : null}
          {rows.length > 0 ? (
            <p className="px-4 pt-2 pb-1 text-ink-2 text-sm leading-snug lg:px-5">
              {trendEnoughText()}
            </p>
          ) : null}
        </>
      )}
      <button
        className="a-press flex min-h-12 w-full items-center gap-3 border-line border-t px-4 py-3 text-left font-semibold text-ink text-sm lg:px-5"
        data-testid="photo-series-open"
        onClick={props.onOpen}
        type="button"
      >
        <span className="flex-1">{seriesOpenText()}</span>
        <Icon className="text-ink-3" icon={ChevronRight} size={18} />
      </button>
    </section>
  );
}

/**
 * The card as a screen mounts it: its own reads, its own age gate, its own way
 * into `?overlay=body`.
 *
 * Both queries are the SAME keys the profile and the overlay already use, so
 * mounting this on Progress costs one request the first time and a cache hit
 * afterwards. `null` while either is unread, and `null` under 15.
 */
export function PhotoSeriesSection(props: {
  readonly className?: string;
  readonly now: Date;
}) {
  const api = useApi();
  const openLayer = useOpenLayer();
  const profile = useDataQuery(profileOptions(api));
  const assessments = useDataQuery(bodyAssessmentsOptions(api));
  const open = useCallback(
    // `section` and `edit` belong to the overlay that is being left; carrying
    // them into `overlay=body` would leave them in the URL for no reader.
    () => openLayer({ edit: undefined, overlay: "body", section: undefined }),
    [openLayer]
  );
  if (!photoSeriesVisible(profile.data?.dateOfBirth, props.now)) {
    return null;
  }
  return (
    <PhotoSeriesCard
      assessments={assessments.data?.items ?? []}
      className={props.className}
      onOpen={open}
    />
  );
}
